import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://ptitservices.com';
const SITE_NAME = 'Ptitservices.com';
const BRAND_SUFFIX = ' | Ptitservices';
const MAX_TITLE = 60;
const DEFAULT_DESC = 'Facilitateur de vies — Services à la personne dans l\'Artois. Ménage, jardinage, repassage, petit bricolage. Crédit d\'impôt 50% avec avance immédiate. Easer dédié.';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png';

/** Keep <title> within Google's ~60 character display limit: add the brand suffix only when it fits, otherwise trim on a word boundary. */
export function buildTitle(title) {
  if (!title) return `Ménage et jardinage à domicile Artois${BRAND_SUFFIX}`;
  const clean = title.replace(/\s+/g, ' ').trim();
  if (clean.length + BRAND_SUFFIX.length <= MAX_TITLE) return `${clean}${BRAND_SUFFIX}`;
  if (clean.length <= MAX_TITLE) return clean;
  const cut = clean.slice(0, MAX_TITLE - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 30 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:—–-]+$/, '')}…`;
}

/** Strip undefined / null / empty values so the emitted JSON-LD never contains invalid empty properties. */
export function cleanSchema(value) {
  if (Array.isArray(value)) return value.map(cleanSchema).filter((v) => v !== undefined);
  if (value && typeof value === 'object') {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      const cleaned = cleanSchema(v);
      const isEmpty = cleaned === undefined || cleaned === null || cleaned === '' || (Array.isArray(cleaned) && cleaned.length === 0);
      if (!isEmpty) out[k] = cleaned;
    });
    return out;
  }
  return value === null ? undefined : value;
}

export function SEOHead({ 
  title, 
  description = DEFAULT_DESC, 
  path = '', 
  type = 'website',
  schema = null,
  noindex = false,
  image = LOGO_URL,
}) {
  const fullTitle = buildTitle(title);
  const url = `${SITE_URL}${path}`;
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]).map(cleanSchema) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {schemas.map((s, i) => (
        <script key={`ld-${i}`} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

// Pre-built schemas
export const SAP_NUMBER = 'SAP991703604';

export function localBusinessSchema(agency, options = {}) {
  const { facebookUrl = 'https://www.facebook.com/artois.ptitservicescom/', rating = null } = options;
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/agence/${agency.id}`,
    "name": `Ptitservices.com - ${agency.name}`,
    "description": `Services à la personne à ${agency.city} et environs. Ménage, jardinage, repassage, petit bricolage à domicile. Crédit d'impôt 50% avec avance immédiate. Easer dédié.`,
    "url": `${SITE_URL}/agence/${agency.id}`,
    "telephone": agency.phoneRaw ? `+33${agency.phoneRaw.substring(1)}` : '',
    "email": agency.email,
    "identifier": {
      "@type": "PropertyValue",
      "propertyID": "Déclaration Services à la Personne",
      "value": SAP_NUMBER
    },
    "logo": LOGO_URL,
    "sameAs": facebookUrl ? [facebookUrl] : undefined,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": agency.address,
      "addressLocality": agency.city.split(' ').slice(1).join(' '),
      "postalCode": agency.city.split(' ')[0],
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": agency.lat,
      "longitude": agency.lng
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": agency.lat,
        "longitude": agency.lng
      },
      "geoRadius": `${agency.radiusKm * 1000}`
    },
    "priceRange": "€€",
    "openingHours": ["Mo-Fr 08:00-18:00", "Sa 09:00-12:00"],
    "image": "https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png",
  };
  if (rating && rating.count > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": String(rating.value),
      "reviewCount": String(rating.count),
      "bestRating": "5"
    };
  }
  return schema;
}

export function organizationSchema() {
  return {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    "name": SITE_NAME,
    "url": SITE_URL,
    "logo": { "@type": "ImageObject", "url": LOGO_URL },
    "telephone": "+33374474782",
    "email": "contact@artois.ptitservices.com",
    "address": { "@type": "PostalAddress", "streetAddress": "61 rue de la mairie", "addressLocality": "Servins", "postalCode": "62530", "addressCountry": "FR" },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({ "@type": "ListItem", "position": i + 1, "name": it.name, "item": `${SITE_URL}${it.path}` })),
  };
}

/** "45 min", "1h30", "2 heures" -> ISO 8601 duration (PT45M, PT1H30M, PT2H); returns undefined when unparseable. */
export function toIsoDuration(label) {
  if (!label) return undefined;
  const str = String(label).toLowerCase();
  const h = str.match(/(\d+)\s*(h|heure)/);
  const m = str.match(/(\d+)\s*(min|mn)/) || (h ? str.match(/h\s*(\d{1,2})\b/) : null);
  const hours = h ? parseInt(h[1], 10) : 0;
  const minutes = m ? parseInt(m[1], 10) : 0;
  if (!hours && !minutes) return undefined;
  return `PT${hours ? `${hours}H` : ''}${minutes ? `${minutes}M` : ''}`;
}

export function serviceSchema(service) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}/services/${service.id}`,
    "url": `${SITE_URL}/services/${service.id}`,
    "name": service.title,
    "serviceType": service.title,
    "description": service.description,
    "provider": organizationSchema(),
    "areaServed": [{ "@type": "AdministrativeArea", "name": "Artois" }, { "@type": "AdministrativeArea", "name": "Pas-de-Calais" }],
    "offers": {
      "@type": "Offer",
      "price": service.priceAfter,
      "priceCurrency": "EUR",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": service.priceAfter,
        "priceCurrency": "EUR",
        "unitText": "heure",
        "description": "Tarif après avance immédiate du crédit d'impôt 50%"
      }
    }
  };
}

export function faqSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  };
}
